// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import {
    getWishListsDataView,
    getWishListState,
} from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { wishListsParameter } from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishLists";
import addAllWishListLines from "@insite/client-framework/Store/Pages/MyLists/Handlers/AddAllWishListLines";
import addWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/AddWishList";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onCancel: () => void;
    onSubmit: (name: string, wishListId: string) => void;
    enableCloseCopyListModalOnEsc: () => void;
    disableCloseCopyListModalOnEsc: () => void;
}

interface State {
    name: string;
    nameError?: React.ReactNode;
    wishListId?: string;
    destinationWishList?: WishListModel;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishLists: getWishListsDataView(state, wishListsParameter).value,
    wishList: getWishListState(state, state.pages.myListDetails.wishListId).value,
    wishListLines: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter).value,
    wishListsParameter,
});

const mapDispatchToProps = {
    addAllWishListLines,
    addWishList,
    loadWishLists,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface MyListsDetailsCopyListFormStyles {
    container?: GridContainerProps;
    nameGridItem?: GridItemProps;
    nameTextField?: TextFieldProps;
    wishListGridItem?: GridItemProps;
    wishListDynamicDropdown?: DynamicDropdownPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    createButton?: ButtonPresentationProps;
}

export const copyListFormStyles: MyListsDetailsCopyListFormStyles = {
    container: {
        gap: 30,
        css: css`
            && div.modal-actions {
                padding-left: 15px;
                @media (max-width: 481px) {
                    padding-right: 15px;
                }
            }
        `,
    },
    nameGridItem: {
        width: 12,
    },
    nameTextField: {
        border: "rectangle",
    },
    wishListGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = copyListFormStyles;

class MyListsDetailsCopyListForm extends React.Component<Props, State> {
    readonly CREATE_ID = "CREATE_ID";

    constructor(props: Props) {
        super(props);
        this.state = {
            name: "",
            wishListId: this.CREATE_ID,
        };
    }

    componentDidMount() {
        if (!this.props.wishLists) {
            this.props.loadWishLists(this.props.wishListsParameter);
        }
    }

    submitHandler = () => {
        if (this.state.wishListId === this.CREATE_ID) {
            if (this.state.name.trim() === "") {
                this.setState({ nameError: siteMessage("Lists_Enter_New_Wishlist_Name") });
                return;
            }

            // <xng-change>
            const groups = JSON.parse(this.props.wishList?.properties?.groups || "[]");
            this.props.addWishList({
                apiParameter: { name: this.state.name, groups } as any,
                onSuccess: this.onCreateSuccess,
                onError: this.onCreateError,
                onComplete(resultProps) {
                    if (resultProps.result?.wishListModel) {
                        this.onSuccess?.(resultProps.result?.wishListModel);
                    } else if (resultProps.result?.errorMessage) {
                        this.onError?.(resultProps.result.errorMessage);
                    }
                },
            });
        } else if (this.props.wishLists) {
            const destinationWishList = this.props.wishLists.find(w => w.id === this.state.wishListId);
            if (destinationWishList) {
                this.setState({ destinationWishList });
                this.copyWishListTo(destinationWishList);
            }
        }
    };

    onCreateSuccess = (addedWishList: WishListModel) => {
        this.setState({ destinationWishList: addedWishList });
        this.copyWishListTo(addedWishList);
    };

    onCreateError = (errorMessage: string) => {
        this.setState({ nameError: errorMessage });
    };

    copyWishListTo = (destinationWishList: WishListModel) => {
        if (destinationWishList && this.props.wishList && this.props.wishListLines) {
            this.props.addAllWishListLines({
                apiParameter: {
                    wishList: destinationWishList,
                    copyFromWishListId: this.props.wishList.id,
                    refreshWishListHeaderGroups: true,
                } as any,
                onSuccess: () => {
                    this.props.toaster.addToast({
                        body: translate("Item(s) copied to ") + destinationWishList.name,
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (!resultProps?.apiResult?.error) {
                        this.onSuccess?.();
                    }
                },
            });
        }
        this.props.onSubmit(this.state.name, this.state.wishListId as string);
    };

    nameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (
            this.props.wishLists &&
            this.props.wishLists.filter(
                wishlist => wishlist.name.toLowerCase() === event.target.value.trim().toLowerCase(),
            )?.length > 0
        ) {
            this.setState({ nameError: "List name already exists" });
            return;
        }
        this.setState({
            name: event.target.value,
            nameError: "",
        });
    };

    wishListChangeHandler = (wishListId?: string) => {
        this.setState({ wishListId });
        this.setState({
            nameError: "",
        });
    };

    render() {
        let options: OptionObject[] = [{ optionText: translate("Create New List"), optionValue: this.CREATE_ID }];
        if (this.props.wishLists) {
            const filteredWishLists = this.props.wishLists.filter(
                o => o.id !== this.props.wishList?.id && !o.isAutogenerated,
            );
            options = options.concat(filteredWishLists.map(o => ({ optionText: o.name, optionValue: o.id })));
        }

        return (
            <GridContainer {...styles.container} data-test-selector="copyListModal">
                <GridItem {...styles.descriptionGridItem}>
                    <Typography {...styles.descriptionText}>
                        {translate("Copy items to a new or existing list.")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.wishListGridItem}>
                    <DynamicDropdown
                        label={translate("Select List")}
                        {...styles.wishListDynamicDropdown}
                        onSelectionChange={this.wishListChangeHandler}
                        selected={this.CREATE_ID}
                        options={options}
                        data-test-selector="copyListModal_selectList"
                        onOpen={this.props.disableCloseCopyListModalOnEsc}
                        onClose={this.props.enableCloseCopyListModalOnEsc}
                    />
                </GridItem>
                {this.state.wishListId === this.CREATE_ID && (
                    <GridItem {...styles.nameGridItem}>
                        <TextField
                            label={translate("List Name")}
                            data-test-selector="copyListModal_listName"
                            {...styles.nameTextField}
                            name="name"
                            maxLength={50}
                            error={this.state.nameError}
                            required
                            defaultValue={this.state.name}
                            onChange={this.nameChangeHandler}
                        ></TextField>
                    </GridItem>
                )}
                <GridItem {...styles.buttonsGridItem} className="modal-actions">
                    <Button
                        {...styles.createButton}
                        onClick={this.submitHandler}
                        disabled={(this.state.nameError as string)?.length > 0}
                        data-test-selector="copyListModal_copyToListButton"
                    >
                        {translate("Copy to List")}
                    </Button>
                    <Button {...styles.cancelButton} onClick={this.props.onCancel}>
                        {translate("Cancel")}
                    </Button>
                </GridItem>
            </GridContainer>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(MyListsDetailsCopyListForm));
